import { useQuery } from '@tanstack/react-query'
import { createFileRoute } from '@tanstack/react-router'
import { ArrowRight, CalendarDays, Heart, MapPin, Send, Users, X } from 'lucide-react'
import { useRef, useState } from 'react'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { publicRsvpQueryOptions } from '@/features/guests/api'
import { PublicRsvpForm } from '@/features/guests/public-rsvp-form'
import { PublicRsvpLandingInfo } from '@/features/guests/public-rsvp-landing-info'
import { ApiError } from '@/lib/api'

export const Route = createFileRoute('/_public/rsvp/$token')({
  component: RsvpPage,
})

const heroImage =
  'https://images.unsplash.com/photo-1465495976277-4387d4b0b4c6?auto=format&fit=crop&w=1600&q=80'
const fullDateFormatter = new Intl.DateTimeFormat('cs-CZ', { dateStyle: 'long' })

function getErrorMessage(error: unknown, fallback: string) {
  if (error instanceof ApiError) {
    return error.problem.detail ?? error.problem.title
  }

  if (error instanceof Error) {
    return error.message
  }

  return fallback
}

function formatDate(value?: string | null) {
  if (!value) {
    return null
  }

  return fullDateFormatter.format(new Date(value))
}

function RsvpPage() {
  const { token } = Route.useParams()
  const entryQuery = useQuery(publicRsvpQueryOptions(token))
  const entry = entryQuery.data
  const questionnaireRef = useRef<HTMLElement>(null)
  const [questionnaireOpen, setQuestionnaireOpen] = useState(false)

  function openQuestionnaire() {
    setQuestionnaireOpen(true)
    window.requestAnimationFrame(() => {
      questionnaireRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' })
    })
  }

  if (entryQuery.isPending) {
    return (
      <div className="grid min-h-dvh place-items-center bg-background p-6">
        <div className="h-64 w-full max-w-xl animate-pulse rounded-lg border bg-card" />
      </div>
    )
  }

  if (entryQuery.isError || !entry) {
    return (
      <div className="grid min-h-dvh place-items-center bg-background p-6">
        <div className="w-full max-w-md rounded-lg border bg-card p-6 shadow-sm">
          <span className="flex size-12 items-center justify-center rounded-lg bg-destructive/10 text-destructive">
            <X className="size-6" />
          </span>
          <h1 className="mt-5 font-heading text-2xl text-foreground">Pozvánka není dostupná</h1>
          <p className="mt-2 text-sm leading-6 text-muted-foreground">
            {getErrorMessage(entryQuery.error, 'Odkaz je neplatný nebo už není aktivní.')}
          </p>
        </div>
      </div>
    )
  }

  const weddingDate = formatDate(entry.website.weddingDate)
  const guestName = [entry.guest.firstName, entry.guest.lastName].filter(Boolean).join(' ')

  return (
    <main className="min-h-dvh bg-background">
      <section className="relative flex min-h-[82dvh] items-end overflow-hidden">
        <img
          src={heroImage}
          alt=""
          className="absolute inset-0 size-full object-cover"
          aria-hidden="true"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/35 to-black/20" />
        <div className="relative mx-auto w-full max-w-5xl px-6 pt-28 pb-12 text-white md:px-10">
          <Badge className="border-white/20 bg-white/15 text-white backdrop-blur" variant="outline">
            Pozvání na svatbu
          </Badge>
          <p className="mt-5 text-sm font-semibold uppercase tracking-[0.16em] text-white/80">
            Milý/á {guestName}
          </p>
          <h1 className="mt-5 font-heading text-5xl leading-tight md:text-7xl">
            {entry.website.brideName} & {entry.website.groomName}
          </h1>
          <p className="mt-6 max-w-2xl text-base leading-7 text-white/85 md:text-lg">
            Srdečně vás zveme, abyste s námi oslavili náš svatební den. Budeme rádi, když nám přes
            krátký dotazník dáte vědět, jestli dorazíte a zda přijdete s doprovodem.
          </p>
          <div className="mt-6 flex flex-wrap gap-3 text-sm font-medium md:text-base">
            {weddingDate && (
              <span className="inline-flex items-center gap-2 rounded-full bg-white/15 px-4 py-2 backdrop-blur">
                <CalendarDays className="size-4" />
                {weddingDate}
              </span>
            )}
            {entry.website.region && (
              <span className="inline-flex items-center gap-2 rounded-full bg-white/15 px-4 py-2 backdrop-blur">
                <MapPin className="size-4" />
                {entry.website.region}
              </span>
            )}
          </div>
          <Button type="button" size="lg" className="mt-8" onClick={openQuestionnaire}>
            Vyplnit dotazník
            <ArrowRight className="size-4" />
          </Button>
        </div>
      </section>

      <section className="mx-auto grid w-full max-w-5xl gap-3 px-6 py-8 md:grid-cols-3 md:px-10">
        <PublicRsvpLandingInfo
          icon={Heart}
          title="Pozvání"
          text="Těšíme se, že náš den prožijeme s vámi."
        />
        <PublicRsvpLandingInfo
          icon={Users}
          title="Účast"
          text="Stačí potvrdit, jestli dorazíte, a případný doprovod."
        />
        <PublicRsvpLandingInfo
          icon={Send}
          title="Odeslání"
          text="Odpověď se uloží rovnou do svatebního plánovače."
        />
      </section>

      {questionnaireOpen && (
        <section ref={questionnaireRef} className="mx-auto w-full max-w-5xl px-6 py-10 md:px-10">
          <PublicRsvpForm token={token} entry={entry} />
        </section>
      )}
    </main>
  )
}
